import '../../css/popup.css';

import { dragElement } from '../drag';
import { getDrawingPoint } from './draw';
import { map } from './tools';
import {
  getAreaWithUnit, getHighlightedFeature,
  highlightFeature, intersection, removeHighlight
} from './feature-tools';
import {
  getFeatureInfo, getLayerById, getSpecificFeatureInfo, getWMSes
} from './layer-tools';
import { getWMS } from './layers';
import { translate } from '../formal';
import {
  cancelAllPopupCalls, getEmissions, sendFeaturesToBackend
} from '../request/computation';
import {
  highlightCorrespondingListItem, removeListItemHighlight
} from '../list';
import { highlightStyle } from './styles';
import { totalEmissionsFromTable } from '../table';
import { writeObject, separateObjectKeysStartingWith } from '../util';

import Overlay from 'ol/Overlay';
import WKB from 'ol/format/WKB';
import { Vector as VectorSource } from 'ol/source';
import { Vector as VectorLayer } from 'ol/layer';

const popupElement = document.getElementById('popup');

let positionOnce = true;
const showPopup = (popup, coordinates) => {
  popup.setPosition(coordinates);
  popupElement.hidden = false;
  const h = popupElement.offsetHeight;
  const w = popupElement.offsetWidth;
  if (positionOnce) {
    popupElement.style.top = `min(100% - 40px, 100% - ${h}px - 0.5em)`;
    popupElement.style.left = `min(100% - 40px, 100% - ${w}px - 0.5em)`;
    positionOnce = false;
  } else { // increase/decrease according to delayed WMS info
    const t = popupElement.style.top;
    const l = popupElement.style.left;
    popupElement.style.top = `min(100% - 40px, 100% - ${h}px - 0.5em, ${t})`;
    popupElement.style.left = `min(100% - 40px, 100% - ${w}px - 0.5em, ${l})`;
  }
};

const getAdvancedProperties = (plan) => {
  const adv = {};
  const p = plan.getProperties();
  const fid = plan.getId();
  if ((p.plannavn || p.drawn) && p.planid) adv.planid = p.planid;
  if (p.plannavn) adv.plannavn = p.plannavn;
  if (plantypeText[p.plantype]) adv.plantype = plantypeText[p.plantype];
  if (planstatusText[p.planstatus]) adv.planstatus = planstatusText[p.planstatus];
  if (p.ikrafttredelsesdato) adv.ikrafttredelsesdato = p.ikrafttredelsesdato;
  if (p.plannavn && p.kommune) adv.kommune = p.kommune;
  if (p.area) adv.areal = `<span id="a_${fid}">${nf(p.arealm2)} m²</span>`;
  if (p.suitable && (p.formål || p.drawn)) { // show filtered/advanced properties:
    const codeYear = (p.objtype === 'RbFormålOmråde') ? 1985 : 2008;
    if (!p.drawn) adv.plantype = p.objtype + ' (PBL ' + codeYear + ')';
    if (p.feltnavn) adv.feltnavn = p.feltnavn;
    if (p.plankategori) adv.plankategori = p.plankategori.replace('de', 'de bruk');
    if (p.arealformål) adv.arealformål = translate(p.arealformål, p.plankategori);
    // Klimagassutslipp:
    const loggedIn = wizard.classList.contains('logged-in');
    const br = 'beregner...';
    const pk = 'pålogging kreves';
    const ph = loggedIn ? br : pk;
    [5, 20, 75].forEach(y => adv[`utslippseffekt på ${y} år`] = `
      <span id="ut${y}_${fid}">${p['utslipp' + y] || ph}</span>
    `);
    // From here on, we save properties (_...) which belongs to intersection:
    // arealkategori (from WMS)
    adv['_nåsituasjon'] = '<span class="_ns"></span>';
    // kat_nivå2 (from feature)
    adv['_planlagt situasjon'] = p.kat_nivå2 || 'Uendret';
    adv['_areal'] = `<span id="_a_${fid}"></span>`;
    const gk = getLayerById('grunnkart').getVisible();
    const gkp = 'grunnkartet må være på';
    const _ph = loggedIn ? (gk ? br : gkp) : pk;
    adv['_utslippseffekt på 5 år'] = `<span id="_ut5_${fid}">${_ph}</span>`;
    adv['_utslippseffekt på 20 år'] = `<span id="_ut20_${fid}">${_ph}</span>`;
    adv['_utslippseffekt på 75 år'] = `<span id="_ut75_${fid}">${_ph}</span>`;
  }
  return adv;
};

const write = (tag, text, parent) => {
  const e = document.createElement(tag);
  e.innerHTML = text;
  parent.appendChild(e);
  return e;
};

const singleFeatureEmissions = (f) => {
  const fid = f.getId();
  const fp = f.getProperties();
  if (!fp.utslipp5 && fp.suitable && (fp.formål || fp.drawn)) {
    const ut5 = document.getElementById('ut5_' + fid);
    const ut20 = document.getElementById('ut20_' + fid);
    const ut75 = document.getElementById('ut75_' + fid);
    let utext = ['ulovlig område', 'ulovlig område', 'ulovlig område'];
    sendFeaturesToBackend([f]).then(
      id => getEmissions(id, false, false, 'samlet (plan - nå)').then(
        promised => utext = totalEmissionsFromTable(promised)
    ).catch(e => console.log('Feil', e)).finally(() => {
      // null if you click on another feature meanwile
      if (ut5) { ut5.innerHTML = utext[0]; f.set('utslipp5', utext[0]); }
      if (ut20) { ut20.innerHTML = utext[1]; f.set('utslipp20', utext[1]); }
      if (ut75) { ut75.innerHTML = utext[2]; f.set('utslipp75', utext[2]); }
    })).catch(e => console.log('Feil', e));
  }
};

const auxiliaryAttributes = [
  'checked', 'drawn', 'highlight', 'id', 'opacity', 'suitable', 'title'
];
const filterProperties = object => {
  auxiliaryAttributes.forEach(f => delete object[f]);
  return object;
};

const displayFeatureInfo = (features, grunnkartIsAvailable) => {
  // write('h3', '#Objekt: ' + features.length, popupContent);
  features.forEach((f, i) => {
    // write('b', 'Objekt ' + (i + 1) + ':', popupContent);
    const adv = getAdvancedProperties(f);
    const plan = adv.plankategori;
    const t = plan ? 'Arealformål plan' : 'Planområde';
    const [blue, pink] = separateObjectKeysStartingWith(adv, '_');
    write('b', t + ':', popupContent);
    const blueList = writeObject(blue, popupContent);
    if (plan) blueList.classList.add('feature-emissions');
    singleFeatureEmissions(f);

    const p = write('p', '', popupContent);
    const hideP = write('a', '<u>Vis/Skjul originale egenskaper</u>', p);
    hideP.style.cursor = 'pointer';
    hideP.style.color = 'gray';
    const fp = filterProperties(f.getProperties());
    const pList = writeObject(fp, popupContent);
    if (Object.keys(adv).length > 0) pList.hidden = true;
    hideP.onclick = () => pList.hidden = !pList.hidden;

    if (plan && grunnkartIsAvailable) {
      const saltvann = (grunnkartIsAvailable.reduce((s, a) => s + a, 0) === 0);
      if (saltvann) { // [0, 0, 0, 0]
        console.log('hav/saltvann');
        pink.nåsituasjon = pink.nåsituasjon.replace(
          '"_ns"><', '"_ns_hav">Hav/Saltvann (utslipp beregnes ikke)<'
        );
        delete pink.areal;
        delete pink['utslippseffekt på 5 år'];
        delete pink['utslippseffekt på 20 år'];
        delete pink['utslippseffekt på 75 år'];
      }
      // Intersection of grunnkart with single feature:
      write('b', 'Grunnkart klippet til plankart:', popupContent);
      const pinkList = writeObject(pink, popupContent);
      if (!saltvann) pinkList.classList.add('intersection-emissions');
    }
    if (i + 1 < features.length) write('hr', '', popupContent);
  });
};

const featureFromWkb = wkb => (new WKB).readFeature(wkb.replace('\\x', ''), {
  featureProjection: map.getView().getProjection().getCode()
});

const showWkb = (wkbFeature, wmsId, wkbLayer) => {
  if (wkbLayer) wkbLayer.getSource().addFeature(wkbFeature);
  else map.addLayer(new VectorLayer({
    properties: { id: `wkb_${wmsId}`, type: 'wkb' },
    source: new VectorSource({ features: [ wkbFeature ] }),
    // style: highlightStyle('fuchsia', [10]),
    zIndex: 1
  }));
};

const intersectionEmissions = (feature, wmsId, wkbFeature, wkbLayer) => {
  if ((feature.get('arealformål') || feature.get('drawn'))) {
    const wkbIntersection = intersection(feature, wkbFeature);
    wkbIntersection.setStyle(highlightStyle('fuchsia', [10]));
    showWkb(wkbIntersection, wmsId, wkbLayer);
    wkbIntersection.set('title', 'klipp'); // avoid backend null error
    wkbIntersection.set('arealformål', feature.get('arealformål'));
    wkbIntersection.set('plankategori', feature.get('plankategori'));
    const fid = feature.getId();
    const _a = document.getElementById('_a_' + fid);
    if (_a) {
      const [area, unit] = getAreaWithUnit(wkbIntersection);
      _a.innerHTML = `${nf(area)} ${unit}`;
    }

    // Emissions of intersection between gk wkb and plan feature:
    sendFeaturesToBackend([wkbIntersection]).then(id =>
      getEmissions(id, false, false, 'samlet (plan - nå)').then(promised => {
        const [t5, t20, t75] = totalEmissionsFromTable(promised);
        const ut5_intersec = document.getElementById('_ut5_' + fid);
        const ut20_intersec = document.getElementById('_ut20_' + fid);
        const ut75_intersec = document.getElementById('_ut75_' + fid);
        // null if you click on another feature meanwile
        if (ut5_intersec) ut5_intersec.innerHTML = t5;
        if (ut20_intersec) ut20_intersec.innerHTML = t20;
        if (ut75_intersec) ut75_intersec.innerHTML = t75;
      }).catch(e => console.log('Feil', e))
    ).catch(e => console.log('Feil', e));
  }
};

const displayVisibleWmsInfo = (wmses, coordinates, pixel, popup, f) => {
  wmses.forEach(wms => {
    const wmsId = wms.get('id');
    const wkbLayer = getLayerById(`wkb_${wmsId}`);
    if (wkbLayer) wkbLayer.getSource().clear();
    const data = wms.getData(pixel);
    if (wms.getVisible() && data && data[3] > 0) {
      // console.log(wms.get('id'));
      getFeatureInfo(coordinates, map.getView(), wms).then(html => {
        if (html) {
          if (popupContent.childNodes.length) write('hr', '', popupContent);
          // popupContent.innerHTML += htmlString;
          // popupContent.appendChild(html.body);
          const table = html.body.querySelector('table');
          if (table) {
            popupContent.appendChild(table);
            showPopup(popup, coordinates);
            const wkb = table.querySelector('#wkb')?.innerHTML;
            if (wkb) {
              const wkbFeature = featureFromWkb(wkb);
              if (f) {
                // console.log('Innenfor, interseksjon mellom f og wkb fra wms');
                intersectionEmissions(f, wmsId, wkbFeature, wkbLayer);
              } else {
                // console.log('Utenfor, vis hele wkb fra wms');
                wkbFeature.setStyle(highlightStyle('green', [10]));
                showWkb(wkbFeature, wmsId, wkbLayer);
              }
            }
          }
        }
      }).catch(err => console.log(err));
    }
  });
};

const nestenLike = (a, b) => Math.abs(a - b) < 1e-2; // 1cm toleranse
const coordinateOnBoundary = (c, f) => {
  const cp = f.getGeometry().getClosestPoint(c);
  // return cp[0] === c[0] && cp[1] === c[1];
  return nestenLike(cp[0], c[0]) && nestenLike(cp[1], c[1]);
};

export const createPopup = () => {
  const markerElement = document.getElementById('popupMarker');
  const x = document.getElementById('popupCloser');

  dragElement('popup', 'popupTopHandle');
  dragElement('popup', 'popupLeftHandle');

  const popup = new Overlay({
    autoPan: true, element: markerElement, offset: [-14, -30], stopEvent: false
  });

  const hidePopup = () => {
    popup.setPosition(undefined);
    popupElement.hidden = true;
    removeListItemHighlight();
  };

  x.onclick = () => {
    hidePopup();
    x.blur();
    removeHighlight(); // blått omriss rundt feature
    const wkbLayer = getLayerById('wkb_grunnkart');
    if (wkbLayer) wkbLayer.getSource().clear(); // stiplete rosa eller grønt omriss
    return false;
  };

  map.addOverlay(popup);

  map.on('pointermove', e => {
    const cursorOnFeature = map.hasFeatureAtPixel(e.pixel);
    let cursor = cursorOnFeature ? 'pointer': '';
    if (cursorOnFeature) {
      const p = getDrawingPoint(); // different from e.pixel when it snaps.
      if (p) map.forEachFeatureAtPixel(e.pixel, f => {
        // if (!coordinateOnBoundary(map.getCoordinateFromPixel(e.pixel), f))
        if (!coordinateOnBoundary(p, f)) cursor = 'not-allowed';
      }, { layerFilter: l => ['drawings', 'file'].indexOf(l.get('type')) > -1 });
    }
    map.getViewport().style.cursor = cursor;
  });

  const wmses = getWMSes(map);
  const gk = getWMS('grunnkart');

  map.on('click', e => {
    cancelAllPopupCalls();
    if (!document.getElementById('selection').hidden) return;
    popupContent.innerHTML = '';
    const features = map.getFeaturesAtPixel( // files, drawings and WFSes
      e.pixel, { layerFilter: l => l.get('type') !== 'wkb' }
    ).filter(f => f.get('suitable'));// no internal prop, getFeaturesAtPixel bug
    let nextFeature;
    const h = getHighlightedFeature();
    removeHighlight();
    if (features.length) {
      // kat_nivå2 eller arealkategori per i dag (fra WMSen):
      const ak = 'Arealkategori';
      const dbk = 'Detaljert bebyggelsekategori';
      getSpecificFeatureInfo(
        [ak, dbk], 'grunnkart', e.coordinate, map.getView()
      ).then(info => {
        const e = document.getElementsByClassName('_ns');
        const l = e.length;
        // for (let i = 0; i < l; i++) writeObject(info, e[i]);
        for (let i = 0; i < l; i++) e[i].innerHTML = info[dbk] || info[ak] || '';
      }).catch(e => console.log(e));

      let numberOfFeatures = 0;
      const hitFeatures = [];
      let relativePosition = -1;

      map.forEachFeatureAtPixel(e.pixel,
        (f, l) => { // To stop detection, return a truthy value.
          if (f.get('planid') && (f.get('arealformål') || f.get('drawn'))) {
            numberOfFeatures += 1;
            hitFeatures.push(f);
            if (h?.get('id') === f.get('id')) {
              relativePosition = numberOfFeatures - 1;
            }
          } else highlightCorrespondingListItem(f);
        },
        { layerFilter: l => ['drawings', 'file'].indexOf(l.get('type')) > -1 }
      );

      const hit = (numberOfFeatures > 0);
      if (hit) {
        const nextPosition = (relativePosition + 1) % numberOfFeatures;
        // console.log(nextPosition, numberOfFeatures);
        nextFeature = hitFeatures[nextPosition];
        if (nextFeature.get('arealformål') || nextFeature.get('drawn'))
          highlightFeature(nextFeature, 'blue');
        if (nextFeature.get('planid'))
          highlightCorrespondingListItem(nextFeature);
        // if (numberOfFeatures > 1)
        //   console.log(`Feature ${nextPosition + 1} of ${numberOfFeatures}`);
      }

      displayFeatureInfo(
        features.filter(f => hitFeatures.indexOf(f) < 0 || nextFeature === f),
        gk.getVisible() && gk.getData(e.pixel)
      );
      showPopup(popup, e.coordinate);
    } else hidePopup();
    // They do not work well here because of asyncronous fetch:
    // if (popupContent.innerHTML === '') hidePopup();
    // else popup.setPosition(e.coordinate);

    displayVisibleWmsInfo(wmses, e.coordinate, e.pixel, popup, nextFeature);
  });
};

const nf = (number) => number.toLocaleString('no-NO'); // Norwegian format

export const updatePopupFeature = (f) => {
  const [area, unit] = getAreaWithUnit(f);
  f.set('arealm2', area);
  f.unset('utslipp5');
  f.unset('utslipp20');
  f.unset('utslipp75');
  if (!popupElement.hidden) {
    const fid = f.getId();
    const a = document.getElementById('a_' + fid);
    if (a) { // feature values are shown on popup, update area and utslipp
      a.innerHTML = `${nf(area)} ${unit}`;
      const ut5 = document.getElementById('ut5_' + fid);
      const ut20 = document.getElementById('ut20_' + fid);
      const ut75 = document.getElementById('ut75_' + fid);
      if (ut5) ut5.innerHTML = 'beregner...';
      if (ut20) ut20.innerHTML = 'beregner...';
      if (ut75) ut75.innerHTML = 'beregner...';
      singleFeatureEmissions(f);
    }
  } // else console.log('Popup is not visible');
};

const plantypeText = {
  30: 'Eldre reguleringsplan',
  31: 'Mindre reguleringsendring',
  32: 'Bebyggelsesplan ihht. reguleringsplan',
  33: 'Bebyggelsesplan ihht. kommuneplan',
  34: 'Områderegulering',
  35: 'Detaljregulering'
};

const planstatusText = {
  1: 'Planlegging igangsatt',
  2: 'Planforslag',
  3: 'Endelig vedtatt arealplan ',
  4: 'Opphevet',
  5: 'Utgått/erstattet',
  6: 'Vedtatt plan med utsatt rettsvirkning',
  7: 'Endelig vedtatt plan underlagt tidsbegrensning',
  8: 'Overstyrt'
};
